import { db } from "services/firebase";
import { isFirestorePermissionDenied } from "utils/firestoreErrors";
import { listTciRegistry } from "./fdeRegistryService";

function asDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds != null) return new Date(value.seconds * 1000);
  return null;
}

export async function listPendingSharingRequests() {
  const mapDocs = (docs) =>
    docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      requestedAtDate: asDate(doc.data()?.requestedAt),
    }));

  try {
    const snap = await db
      .collection("tciSharingRequests")
      .where("status", "==", "pending")
      .orderBy("requestedAt", "asc")
      .get();
    return mapDocs(snap.docs);
  } catch (err) {
    console.warn("[listPendingSharingRequests] ordered query failed", err);
    // Permission-denied must not look like an empty queue — rethrow for UI.
    if (isFirestorePermissionDenied(err)) throw err;
    try {
      const snap = await db
        .collection("tciSharingRequests")
        .where("status", "==", "pending")
        .get();
      return mapDocs(snap.docs).sort(
        (a, b) => (a.requestedAtDate?.getTime() || 0) - (b.requestedAtDate?.getTime() || 0),
      );
    } catch (fallbackErr) {
      console.warn("[listPendingSharingRequests] fallback query failed", fallbackErr);
      throw fallbackErr;
    }
  }
}

export async function listPendingCmrDataFlowRequests() {
  const mapDocs = (docs) =>
    docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      requestedAtDate: asDate(doc.data()?.requestedAt),
    }));

  try {
    const snap = await db
      .collection("cmrDataFlowRequests")
      .where("status", "==", "pending")
      .orderBy("requestedAt", "asc")
      .get();
    return mapDocs(snap.docs);
  } catch (err) {
    console.warn("[listPendingCmrDataFlowRequests] ordered query failed", err);
    if (isFirestorePermissionDenied(err)) throw err;
    try {
      const snap = await db
        .collection("cmrDataFlowRequests")
        .where("status", "==", "pending")
        .get();
      return mapDocs(snap.docs).sort(
        (a, b) => (a.requestedAtDate?.getTime() || 0) - (b.requestedAtDate?.getTime() || 0),
      );
    } catch (fallbackErr) {
      console.warn("[listPendingCmrDataFlowRequests] fallback query failed", fallbackErr);
      throw fallbackErr;
    }
  }
}

export async function listGlobalTciManagers() {
  const snap = await db
    .collection("fdeAssignments")
    .where("role", "==", "global-tci-manager")
    .where("status", "==", "active")
    .get();

  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function resolveInstitutionNames(institutionIds = []) {
  const unique = [...new Set(institutionIds.filter(Boolean))];
  const names = {};
  await Promise.all(
    unique.map(async (institutionId) => {
      try {
        const snap = await db.collection("organizations").doc(institutionId).get();
        names[institutionId] = snap.exists
          ? snap.data()?.name || institutionId
          : institutionId;
      } catch (err) {
        console.warn("[resolveInstitutionNames] skipped org lookup", institutionId, err);
        names[institutionId] = institutionId;
      }
    }),
  );
  return names;
}

export async function listGlobalCatalogTcis() {
  const rows = await listTciRegistry({});
  return rows.filter((row) => row.scope === "global" && row.isActive !== false);
}

export async function lookupUserUidByEmail(email) {
  const normalized = (email || "").trim().toLowerCase();
  if (!normalized) return null;
  const snap = await db.collection("users").where("email", "==", normalized).limit(1).get();
  if (snap.empty) return null;
  return snap.docs[0].id;
}
